import type { PaymentTerms } from '../../api/types';

/** Contract §3: support phone must be E.164. */
export const E164 = /^\+[1-9]\d{7,14}$/;

export function validateSupportPhone(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Support phone is required.';
  if (!E164.test(trimmed)) return 'Enter the number in E.164 format, e.g. +918522803275.';
  return null;
}

export const CAP_MIN = 1;
export const CAP_MAX = 500;

/** Whole number 1..500. Returns the parsed value or an error message. */
export function parseCap(value: string): { value: number } | { error: string } {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed))
    return { error: `Enter a whole number between ${CAP_MIN} and ${CAP_MAX}.` };
  const n = Number(trimmed);
  if (n < CAP_MIN || n > CAP_MAX)
    return { error: `Enter a whole number between ${CAP_MIN} and ${CAP_MAX}.` };
  return { value: n };
}

export const FREE_HOURS_MIN = 0;
export const FREE_HOURS_MAX = 168;

/** Detention free time (rhino-api#378 C1): whole hours 0..168. */
export function parseFreeHours(value: string): { value: number } | { error: string } {
  const trimmed = value.trim();
  const n = Number(trimmed);
  if (!/^\d+$/.test(trimmed) || n < FREE_HOURS_MIN || n > FREE_HOURS_MAX)
    return { error: `Enter a whole number of hours from ${FREE_HOURS_MIN} to ${FREE_HOURS_MAX}.` };
  return { value: n };
}

/** Per-person override: blank = use the platform default (null). */
export function parseCapOverride(value: string): { value: number | null } | { error: string } {
  if (!value.trim()) return { value: null };
  return parseCap(value);
}

// ─── Scorecard targets validation (OPS_SCORECARD_PLAN §2) ────────────────────

export const WEEKLY_FULFILLED_MIN = 1;
export const WEEKLY_FULFILLED_MAX = 1000;
export const CLAIM_TARGET_MIN = 1;
export const CLAIM_TARGET_MAX = 1440;
export const DRIVER_TARGET_MIN = 5;
export const DRIVER_TARGET_MAX = 10080;
export const POD_CLOSE_TARGET_MIN = 1;
export const POD_CLOSE_TARGET_MAX = 720;

export function parseTargetInt(
  value: string,
  min: number,
  max: number,
  label: string,
): { value: number } | { error: string } {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return { error: `${label} must be a whole number between ${min} and ${max}.` };
  }
  const n = Number(trimmed);
  if (n < min || n > max) {
    return { error: `${label} must be between ${min} and ${max}.` };
  }
  return { value: n };
}

export function validatePaymentTerms(value: PaymentTerms): string | null {
  const bounds: Array<[keyof PaymentTerms, string, number, number]> = [
    ['partnerAdvancePct', 'Partner advance', 0, 100],
    ['partnerAdvanceSlaMinutes', 'Advance SLA', 15, 10080],
    ['partnerBalanceSlaHours', 'Balance SLA', 1, 720],
    ['defaultShipperPaymentTermsDays', 'Shipper payment terms', 0, 180],
    ['payoutApprovalTargetMinutes', 'Approval target', 5, 1440],
    ['invoiceTargetHours', 'Invoice target', 1, 720],
  ];
  for (const [key, label, min, max] of bounds) {
    const number = Number(value[key]);
    if (!Number.isInteger(number) || number < min || number > max)
      return `${label} must be a whole number between ${min} and ${max}.`;
  }
  const appBounds: Array<[keyof PaymentTerms, string, number, number]> = [
    ['paymentClaimVerifyTargetHours', 'Claim verification target', 1, 72],
    ['invoiceReminderDaysBefore', 'Invoice reminder days before', 0, 15],
    ['chargeRequestDecideTargetHours', 'Charge request decision target', 1, 168],
  ];
  for (const [key, label, min, max] of appBounds) {
    if (value[key] == null) continue;
    const number = Number(value[key]);
    if (!Number.isInteger(number) || number < min || number > max)
      return `${label} must be a whole number between ${min} and ${max}.`;
  }
  if (
    value.dnmPayeeAccountName != null &&
    (value.dnmPayeeAccountName.trim().length < 1 || value.dnmPayeeAccountName.trim().length > 100)
  )
    return 'DNM account name must contain 1–100 characters.';
  if (value.dnmPayeeAccountNumber != null && !/^\d{6,20}$/.test(value.dnmPayeeAccountNumber.trim()))
    return 'DNM account number must contain 6–20 digits.';
  if (value.dnmPayeeIfsc != null && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.dnmPayeeIfsc.trim()))
    return 'Enter a valid DNM IFSC.';
  if (value.dnmPayeeUpiId && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/.test(value.dnmPayeeUpiId.trim()))
    return 'Enter a valid DNM UPI ID.';
  if (!/^(0[1-9]|[12]\d|3[0-8])$/.test(value.invoiceSellerStateCode))
    return 'Seller state code must be between 01 and 38.';
  if (
    !Number.isFinite(value.defaultInvoiceGstRate) ||
    value.defaultInvoiceGstRate < 0 ||
    value.defaultInvoiceGstRate > 40
  )
    return 'Default GST rate must be between 0 and 40.';
  if (
    value.invoiceGstOptions
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean).length > 6
  )
    return 'Configure at most 6 GST options.';
  const defaultPair = `${value.defaultInvoiceGstMode}:${value.defaultInvoiceGstRate}`.toLowerCase();
  if (
    !value.invoiceGstOptions
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .includes(defaultPair)
  )
    return 'The default GST option must be in the allowed GST options.';
  return null;
}
